import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const ERA_SLUG = (era) => era.toLowerCase().replace(/–/g, "-").replace(/\s+/g, "-");

const AXIS = { stroke: "#3f3f46", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };
const usd = (n) => (Math.abs(n || 0) >= 1e6 ? `$${(n / 1e6).toFixed(1)}m` : `$${Math.round(n || 0).toLocaleString()}`);

/** What Kenyan talent has been worth, era by era: total value as bars, average per valued player as a line. */
export const PlayerValueTimeline = () => {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/player-value-timeline").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d?.rows?.length) return null;
  const chart = d.rows.map((r) => ({
    era: r.era,
    total: +(r.total_usd / 1e6).toFixed(2),
    average: +(r.average_usd / 1e6).toFixed(2),
    players: r.players,
  }));

  return (
    <section className="mt-16" data-testid="player-value-timeline">
      <p className="overline text-ochre">The trend</p>
      <h2 className="display-3 mt-4 max-w-3xl">{d.heading}</h2>
      <p className="body-copy mt-5 max-w-3xl">{d.intro}</p>

      <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="timeline-headline">
        {[["Total published value", usd(d.total_usd)],
          ["Careers in the record", d.players],
          ["Players carrying a value", d.players_valued],
          ["Peak era", d.peak_era || "—"]].map(([k, v]) => (
          <div key={k} className="bg-[#0b0b0b] p-6">
            <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
            <p className="mono mt-3 text-sm text-white">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 h-[300px] w-full border border-white/10 bg-[#0b0b0b] p-5" data-testid="timeline-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart}>
            <CartesianGrid stroke="#1f1f22" vertical={false} />
            <XAxis dataKey="era" {...AXIS} />
            <YAxis {...AXIS} tickFormatter={(v) => `$${v}m`} />
            <Tooltip
              contentStyle={{ background: "#0b0b0b", border: "1px solid #E32118", borderRadius: 0,
                fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}
              labelStyle={{ color: "#E5A91E" }}
              formatter={(v, n) => [`$${v}m`, n === "total" ? "Total value" : "Average per valued player"]}
            />
            <Legend wrapperStyle={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11 }} />
            <Bar dataKey="total" name="Total value" fill="#E32118" />
            <Line dataKey="average" name="Average per valued player" stroke="#E5A91E" strokeWidth={2} dot />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-8 overflow-x-auto border border-zinc-900">
        <table className="w-full min-w-[720px] text-left" data-testid="timeline-table">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["Era", "Careers", "Carrying a value", "Total", "Average", "Highest single value"].map((h) => (
                <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.era} className="border-b border-zinc-900" data-testid={`timeline-row-${r.era.slice(0, 4)}`}>
                <td className="px-4 py-4">
                  <Link to={`/eras/${ERA_SLUG(r.era)}`}
                    data-testid={`timeline-era-link-${r.era.slice(0, 4)}`}
                    className="font-display text-sm uppercase text-white transition-colors hover:text-ochre">
                    {r.era}
                  </Link>
                </td>
                <td className="mono px-4 py-4 text-xs text-zinc-300">{r.players}</td>
                <td className="mono px-4 py-4 text-xs text-zinc-300">{r.players_valued}</td>
                <td className="mono px-4 py-4 text-sm text-ochre">{usd(r.total_usd)}</td>
                <td className="mono px-4 py-4 text-xs text-zinc-300">{usd(r.average_usd)}</td>
                <td className="mono px-4 py-4 text-xs text-zinc-300">
                  {r.top_player_slug ? (
                    <Link to={`/hall-of-fame/${r.top_player_slug}`} className="hover:text-ochre"
                      data-testid={`timeline-top-player-${r.era.slice(0, 4)}`}>
                      {`${r.top_player} · ${usd(r.top_value_usd)}`}
                    </Link>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.note}</p>

      <section className="mt-16" data-testid="value-bases">
        <h3 className="display-3">{d.bases_heading}</h3>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.bases_note}</p>
        <div className="mt-6 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="value-bases-split">
          {[["Reported transfer fees", `${usd(d.fees_usd)} · ${d.fees_count} careers`],
            ["Researched estimates", `${usd(d.estimates_usd)} · ${d.estimates_count} careers`],
            ["Careers with no value published", d.players - d.players_valued],
            ["Licensed data provider used", "None"]].map(([k, v]) => (
            <div key={k} className="bg-[#0b0b0b] p-6">
              <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
              <p className="mono mt-3 text-sm text-white">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 border-t border-zinc-900">
          {(d.bases || []).map((b) => (
            <div key={b.slug} className="grid gap-3 border-b border-zinc-900 py-5 md:grid-cols-[1fr_2fr]"
              data-testid={`value-basis-${b.slug}`}>
              <div>
                <Link to={`/hall-of-fame/${b.slug}`} className="font-display text-sm uppercase text-white hover:text-ochre">
                  {b.name}
                </Link>
                <p className="mono mt-2 text-[0.58rem] uppercase tracking-[0.14em] text-zinc-500">
                  {b.club || "—"} · {b.era}
                </p>
                <p className="mono mt-2 text-sm text-ochre">
                  {b.value_usd}
                  <span className={`ml-3 border px-2 py-0.5 text-[0.5rem] tracking-[0.14em] ${
                    b.value_estimated ? "border-zinc-700 text-zinc-400" : "border-maasai text-maasai"}`}>
                    {b.value_estimated ? "Estimate" : "Transfer fee"}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm leading-relaxed text-zinc-400">{b.value_basis || "Basis not recorded."}</p>
                {b.source_url ? (
                  <a href={b.source_url} target="_blank" rel="noreferrer"
                    data-testid={`value-source-${b.slug}`}
                    className="mono mt-3 inline-flex items-center gap-2 text-[0.58rem] uppercase tracking-[0.16em] text-ochre hover:text-white">
                    {b.source_label} <ExternalLink size={11} />
                  </a>
                ) : (
                  <p className="mono mt-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-600"
                    data-testid={`value-source-missing-${b.slug}`}>
                    No source page recorded
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
      <p className="mono mt-5 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">
        Pick an era above to open its cohort page: what changed in that period, and every player behind the
        number. Each career is counted once.
      </p>
    </section>
  );
};
